import React, { useEffect, useRef, useState } from "react";
import {
  Application,
  Container,
  FederatedPointerEvent,
  Sprite,
  Texture,
  Point,
} from "pixi.js-legacy";
import {
  ChallengeConfig,
  DragObject,
  SolvedChallenge,
} from "../../models/Types";
import ChallengeUtils from "./ChallengeUtils";
import { useTranslation } from "react-i18next";
import { getBackgroundColor } from "../Util";

const BACKGROUND_BLUR_B64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEEAAABBCAYAAACO98lFAAAACXBIWXMAAAsSAAALEgHS3X78AAACx0lEQVR4nO2b71HCQBDFn47foQNjBWIF0gHpAKxASsAKlA6wArED6AAqEDqACs452Tjk5hJy/zbJhd9MPjGeuTd7e7vvLjdCCHSdW8b5JwCGTdT7LuDYEwApgAGAe83vawBLenYB3+Mycjl4fmZCiIMwYyGESAK8S6XH52ADIcTGcPIqszpE8JUYZcivAPQ8jPVJS4kNH4nRpwCSMYCFp7Eq4SpC37MAGVKImecxC3FdDgt64VA8cOwcLpEwDCyA5CPw+H+4iMCRvEZUZAXFVoQ+QxRkpKH/QVnFmFDmHxT8xsWURFeRuWJDjxO6xJhQwntmnKgLexJqaTuGKkK25T3WOStLXmzrCzUnTFoqAGgn0S2bi6giBE9CAenZtuqqCFZKNghdEjcWoa1LwQlOZ6mxdF4EXEU40XkRENhorQNZ5xxMzVs1ErYtF0G62u8Afqh6rNTjqCLUa337ZUzN1fTSqKoI1k1IQ+lRZJT2FLouchNp0VToYut2B1l6vgE4hn8vVgrN20tGa0oPl4vEwZNqxFR1m9tmtJSxVrtNU8s9tMXORc7KN60YZWL5buW08+S2TZvDlz6p6PvUiZPtufdg0zscIqgnciWAbQO18vMuzcBWhBjK6/8dosuttPXukNHIC1iGOIvAepMkAPvzIW1EmBbcRmsTucRuKsKA8wZJQHIimBRLKZXNbS6SQEsh5zhViYSUlPuKQADoIrkoEoa09kcsr8XHVndUp3Ob5enua0QTzzgWHTirkZBS2MfGkaJbe6tFdz8hNvZlAuhEiKESPGdOOaD0XpO6HGL4AuRIrf6saqMX2zHcvMphi0psXeTB5o86fyqNqwgnOi8CNCLEdvRWCVUE53vCNWPlfcYmgpULrorA8pFFIOa+ImFHF6XbxtrF8SryExKqvIYNv7CxJrfL6eu5zn8wjmudcKLzIuAqAgAAvzHoL5448fC8AAAAAElFTkSuQmCC";
const DRAG_THRESHOLD = 3;

enum DragState {
  NOT_DRAGGING = 0,
  STARTED = 1,
  DRAGGING = 2,
}

export default function Challenge2D(props: ChallengeConfig) {
  const gutterTop = 50;
  const challengeRef = useRef<any>(null);
  const { t } = useTranslation() as any;

  const [visualSolution, setVisualSolution] = useState<SolvedChallenge | null>(
    null,
  );
  const puzzle = useRef<Container>(new Container());
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [userHasMovedThePiece, setUserHasMovedThePiece] = useState(false);

  const isProcessing = visualSolution !== null;

  function render2DPuzzle(application: Application) {
    const container = new Container();
    container.eventMode = "dynamic";
    container.cursor = "grab";

    function onDragMove(event: FederatedPointerEvent) {
      const obj = event.currentTarget as DragObject;
      if (!obj.dragging) return;

      const data = obj.dragData;

      if (obj.dragging === DragState.STARTED) {
        if (
          hasExceededDragThreshold(
            data.globalX,
            data.globalY,
            obj.dragGlobalStart,
          )
        ) {
          obj.dragging = DragState.DRAGGING;
        }
        return;
      }

      if (obj.dragging === DragState.DRAGGING) {
        updateObjectPosition(obj, data);
        setUserHasMovedThePiece(true);
      }
    }

    function hasExceededDragThreshold(
      currentX: number,
      currentY: number,
      start: Point,
    ): boolean {
      const deltaX = Math.abs(currentX - start.x);
      const deltaY = Math.abs(currentY - start.y);
      return deltaX + deltaY >= DRAG_THRESHOLD;
    }

    function updateObjectPosition(
      obj: DragObject,
      data: FederatedPointerEvent,
    ) {
      const dragPointerEnd = data.getLocalPosition(obj.parent);
      const deltaX = dragPointerEnd.x - obj.dragPointerStart.x;
      const deltaY = dragPointerEnd.y - obj.dragPointerStart.y;

      obj.position.set(
        obj.dragObjStart.x + deltaX,
        obj.dragObjStart.y + deltaY,
      );
    }

    const puzzleObj = ChallengeUtils.timedCreateSprite(
      props.puzzleImageURL,
      props.onPieceLoadStart,
      props.onPieceLoadEnd,
    );
    puzzleObj.anchor.set(0.5, 0.5);
    puzzleObj.x += 0.25;
    puzzleObj.y += 1;

    // TODO: disable drag-and-drop while processing
    container
      .on("pointerdown", (e: FederatedPointerEvent) => {
        e.stopPropagation();
        container.eventMode = "dynamic";
        e.currentTarget.cursor = "grabbing";
        ChallengeUtils.onDragStart(e);
      })
      .on("pointerup", (e: FederatedPointerEvent) => {
        e.stopPropagation();
        container.cursor = "grab";
        ChallengeUtils.onDragEnd(e);
      })
      .on("globalpointermove", onDragMove);

    const texture = Texture.from(BACKGROUND_BLUR_B64);
    const blurObj = new Sprite(texture);
    blurObj.anchor.set(0.5, 0.5);

    container.addChild(blurObj);
    container.addChild(puzzleObj);

    container.x = 32;
    container.y = 32;

    puzzle.current = container;

    application.stage.addChild(container);
  }

  function initialiseApplication() {
    const view = challengeRef.current;

    if (view && view.children.length === 0) {
      const application: Application = ChallengeUtils.generateApplication({
        width: props.width,
        height: props.height,
        backgroundColor: getBackgroundColor(),
      });

      view.appendChild(application.view);

      const background = ChallengeUtils.timedCreateSprite(
        props.bgImageURL,
        props.onBgLoadStart,
        () => {
          if (props.onBgLoadEnd) {
            props.onBgLoadEnd();
          }
          setNextButtonEnabled(true);
        },
      );

      background.position.y = gutterTop;

      application.stage.addChild(background);

      render2DPuzzle(application);
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (isProcessing) {
      return;
    } else if (e.code === "Enter" || e.code === "Space") {
      commitVisualSolution();
    } else if (e.code === "ArrowUp" || e.code === "ArrowDown") {
      puzzle.current.y += e.code === "ArrowUp" ? -2 : 2;
      setUserHasMovedThePiece(true);
    } else if (e.code === "ArrowLeft" || e.code === "ArrowRight") {
      puzzle.current.x += e.code === "ArrowLeft" ? -2 : 2;
      setUserHasMovedThePiece(true);
    }
  }

  function resetPuzzle() {
    puzzle.current.x = 32;
    puzzle.current.y = 32;
    setUserHasMovedThePiece(false);
  }

  function commitVisualSolution() {
    setVisualSolution({
      x: puzzle.current.x - 32,
      y: puzzle.current.y - gutterTop - 32,
    });
  }

  function submitVisualSolution() {
    if (visualSolution !== null) {
      props.submitVisualSolutionCallback(visualSolution);
    }
  }

  function attachDomEvents() {
    document.addEventListener("keydown", handleKeyDown, false);

    const cleanup = () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
    return cleanup;
  }

  useEffect(submitVisualSolution, [visualSolution]);
  useEffect(attachDomEvents, [challengeRef]);
  useEffect(initialiseApplication, [challengeRef]);

  return (
    <div>
      <div ref={challengeRef} className="challenge-canvas" />

      {!isProcessing && (
        <div>
          <button
            className="btn rounded-full btn-small btn-solid-purple btn-full-width"
            onClick={commitVisualSolution}
            disabled={!nextButtonEnabled}
          >
            {t("Next.text")}
          </button>
          <button
            className={
              "btn btn-small rounded-full btn-full-width btn-outline-grey"
            }
            onClick={() => resetPuzzle()}
            disabled={!userHasMovedThePiece}
          >
            {t("ResetPuzzle.text")}
          </button>
        </div>
      )}
    </div>
  );
}
